// The recording indicator everyone in the call sees. On the first press it
// shows as a full "Recording" pill under the clock for about two seconds, then
// collapses to a dot and slides to the left edge, or to the right of whatever
// sits there (your own video can be dragged into that corner).

import { useEffect, useRef, useState, type JSX } from "react";

const FULL_MS = 2000;
const EDGE = 12;
const GAP = 8;

export function RecordingMark({ recording }: { readonly recording: boolean }): JSX.Element | null {
  const ref = useRef<HTMLDivElement>(null);
  const [full, setFull] = useState(true);
  // null until measured: the pill starts at the top center.
  const [dotLeft, setDotLeft] = useState(EDGE);
  const [centerLeft, setCenterLeft] = useState<number | null>(null);

  useEffect(() => {
    setFull(true);
    if (!recording) return;
    const t = window.setTimeout(() => setFull(false), FULL_MS);
    return () => window.clearTimeout(t);
  }, [recording]);

  useEffect(() => {
    if (!recording) return;
    const place = (): void => {
      const el = ref.current;
      const parent = el?.offsetParent as HTMLElement | null;
      if (!el || !parent) return;
      const box = parent.getBoundingClientRect();
      setCenterLeft(box.width / 2);
      let left = EDGE;
      const dotSize = 26;
      // Left to right, so a name chip and a self view side by side both get cleared.
      const others = [...document.querySelectorAll<HTMLElement>(".selfview, [data-corner-avoid]")].sort(
        (x, y) => x.getBoundingClientRect().left - y.getBoundingClientRect().left,
      );
      for (const other of others) {
        const r = other.getBoundingClientRect();
        const relLeft = r.left - box.left;
        const relRight = r.right - box.left;
        const relTop = r.top - box.top;
        // Only something sitting in the top-left strip is in the way.
        if (relTop < 56 && relLeft < left + dotSize + GAP && relRight > left) left = Math.max(left, relRight + GAP);
      }
      setDotLeft(left);
    };
    place();
    const id = window.setInterval(place, 400);
    window.addEventListener("resize", place);
    return () => {
      window.clearInterval(id);
      window.removeEventListener("resize", place);
    };
  }, [recording]);

  if (!recording) return null;
  return (
    <div
      ref={ref}
      className={`recmark${full ? " recmark--full" : ""}`}
      role="status"
      aria-label="Recording"
      style={{ left: full ? (centerLeft ?? "50%") : dotLeft }}
    >
      <span className="recmark__dot" aria-hidden="true" />
      <span className="recmark__text">Recording</span>
    </div>
  );
}
